import React from 'react';
import SaferSavvyLogo from './SaferSavvyLogo';
import {
  Bus,
  Twitter,
  Facebook,
  Instagram, 
  Linkedin,
  Home,
  Zap,
  Clock,
  Map,
  TrafficCone,
  Briefcase,
  Handshake,
  Mail,
  Shield,
  FileText,
  Cookie
} from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="bg-primary-800 text-white pt-16 pb-8 px-4 sm:px-8 dark:bg-ink-900 dark:border-t dark:border-ink-700/70">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Logo and Description */}
          <div>
            <div className="flex items-center mb-4">
              <SaferSavvyLogo size={140} />
            </div>
            <p className="text-gray-300 mb-6 text-[13px]">
              SaferSavvy — AI-powered university transport automation for smarter, faster, and safer campus commutes.
              Real-time tracking, smart routing, and automated attendance.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://twitter.com/safersavvy" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow SaferSavvy on Twitter"
                className="text-gray-400 hover:text-accent-500 transition-colors duration-300"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com/safersavvy" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow SaferSavvy on Facebook"
                className="text-gray-400 hover:text-accent-500 transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/safersavvy" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow SaferSavvy on Instagram"
                className="text-gray-400 hover:text-accent-500 transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://linkedin.com/company/safersavvy" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow SaferSavvy on LinkedIn"
                className="text-gray-400 hover:text-accent-500 transition-colors duration-300"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="text-[14px] font-semibold mb-4 border-b border-primary-500 dark:border-ink-700 pb-2 flex items-center">
              <Home className="w-4 h-4 mr-2" />
              Quick Links
            </h3>
            <ul className="space-y-2">
              {[
                { name: 'Home' },
                { name: 'Features' },
                { name: 'How It Works' },
                { name: 'About Us' },
                { name: 'Testimonials' },
                { name: 'Contact' },
              ].map((link) => (
                <li key={link.name}>
                  <a 
                    href={`#${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-300 hover:text-accent-500 text-[12px] transition-colors duration-300 flex items-center"
                  >
                    {/* <link.icon className="w-3 h-3 mr-2" /> */}
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Services */}
          <div>
            <h3 className="text-[14px] font-semibold mb-4 border-b border-primary-500 dark:border-ink-700 pb-2 flex items-center">
              <Zap className="w-4 h-4 mr-2" />
              Our Services
            </h3>
            <ul className="space-y-2">
              {[
                { name: 'Route Optimization', icon: Map },
                { name: 'Real-time Tracking', icon: Clock },
                { name: 'Multi-Modal Planning', icon: Bus },
                { name: 'Traffic Predictions', icon: TrafficCone },
                { name: 'Business Solutions', icon: Briefcase },
                { name: 'City Partnerships', icon: Handshake }
              ].map((service) => (
                <li key={service.name}>
                  <a 
                    href="#"
                    className="text-gray-300 text-[12px] hover:text-accent-500 transition-colors duration-300 flex items-center"
                  >
                    {/* <service.icon className="w-3 h-3 mr-2" /> */}
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Newsletter */}
          <div>
            <h3 className="text-[14px] font-semibold mb-4 border-b border-primary-500 dark:border-ink-700 pb-2 flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Stay Updated
            </h3>
            <p className="text-gray-300 mb-4 text-[12.4px]">
              Subscribe to our newsletter for the latest updates and features.
            </p>
            <form className="mb-4">
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="bg-primary-900 text-white px-4 py-2 text-sm rounded-l-lg focus:outline-none focus:ring-1 focus:ring-accent-600 w-full dark:!bg-ink-800 dark:placeholder:text-ink-400 dark:border dark:border-ink-700"
                />
                <button 
                  type="submit"
                  className="bg-accent-700 hover:bg-accent-800 px-4 py-2 rounded-r-lg text-xs transition-colors duration-300 flex items-center"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Subscribe
                </button>
              </div>
            </form>
            <p className="text-[11px] text-gray-400">
              By subscribing, you agree to our Privacy Policy and consent to receive updates from our company.
            </p>
          </div>
        </div>
        
        {/* Horizontal Divider */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-ink-600 my-6"></div>
        
        {/* Bottom Footer */}
        <div className="pt-4 text-xs text-gray-400 flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            &copy; {new Date().getFullYear()} Safar Savvy. All rights reserved.
          </div>
          <div className="flex space-x-6">
            <a href="/privacy-policy" className="hover:text-accent-500 transition-colors duration-300 flex items-center">
              <Shield className="w-3 h-3 mr-1" /> Privacy Policy
            </a>
            <a href="/terms-of-service" className="hover:text-accent-500 transition-colors duration-300 flex items-center">
              <FileText className="w-3 h-3 mr-1" /> Terms of Service
            </a>
            <a href="#" className="hover:text-accent-500 transition-colors duration-300 flex items-center">
              <Cookie className="w-3 h-3 mr-1" /> Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;